/**
 * ForgotPasswordPage & ResetPasswordPage — magic-link flow.
 *
 * /forgot-password:
 *   Enter email → POST /api/users/forgot-password → always returns 200
 *   (no enumeration). Show "se l'email è registrata riceverai un link".
 *
 * /reset-password?token=XXX:
 *   Enter new password → POST /api/users/reset-password {token, password}.
 *   On success redirect to /auth?just_reset=1.
 */
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Mail, Lock, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  bg: "#050507",
  surface: "#0A0A0D",
  border: "rgba(255,255,255,0.08)",
  text: "#FFFFFF",
  muted: "#71717A",
  accent: "#C8FF00",
  red: "#EF4444",
  green: "#22FFA3",
};

const inputStyle = {
  width: "100%", padding: "0.65rem 0.85rem 0.65rem 2.4rem",
  background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}`,
  color: C.text, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.85rem",
  outline: "none",
};
const btnPrimary = {
  width: "100%", padding: "0.75rem", marginTop: "0.7rem",
  background: C.accent, color: "#020202", border: "none",
  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
  letterSpacing: "0.14em", textTransform: "uppercase",
  fontSize: "0.9rem", cursor: "pointer",
};

const Container = ({ children }) => (
  <div style={{ background: C.bg, minHeight: "100vh", color: C.text, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem 1.5rem" }} className="grid-bg">
    <div style={{ width: "min(440px, 100%)", padding: "2rem", background: C.surface, border: `1px solid ${C.border}` }}>
      {children}
    </div>
  </div>
);

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/users/forgot-password`, { email });
    } catch { /* silenzioso: idempotente lato server */ }
    setDone(true);
    setBusy(false);
  };

  return (
    <Container>
      <button onClick={() => navigate("/auth")} data-testid="forgot-back-auth" style={{
        background: "transparent", border: "none", color: C.muted,
        cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
        fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem",
        letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: "1.5rem",
      }}>
        <ArrowLeft size={11} /> Torna al login
      </button>
      <h1 style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900, fontSize: "1.6rem", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "0.4rem" }}>
        Hai dimenticato la <span style={{ color: C.accent }}>password</span>?
      </h1>
      <p style={{ color: C.muted, fontSize: "0.82rem", lineHeight: 1.55, marginBottom: "1.4rem" }}>
        Inserisci la tua email. Se è registrata, riceverai un link per reimpostare la password.
      </p>

      {done ? (
        <div data-testid="forgot-success" style={{
          padding: "1rem", background: "rgba(34,255,163,0.06)",
          border: `1px solid ${C.green}55`, color: C.green,
          fontFamily: "'JetBrains Mono',monospace", fontSize: "0.78rem",
        }}>
          <CheckCircle2 size={14} style={{ display: "inline", marginRight: 6 }} />
          Se l'email è registrata, riceverai il link di reset entro qualche minuto. Controlla anche lo spam.
        </div>
      ) : (
        <form onSubmit={submit}>
          <div style={{ position: "relative", marginBottom: 8 }}>
            <Mail size={13} style={{ position: "absolute", left: 12, top: 12, color: C.muted }} />
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com" data-testid="forgot-email-input" style={inputStyle} />
          </div>
          <button type="submit" disabled={busy} data-testid="forgot-submit" style={btnPrimary}>
            {busy ? <Loader2 size={13} className="animate-spin" style={{ display: "inline" }} /> : "Invia link di reset"}
          </button>
        </form>
      )}
    </Container>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (pwd !== pwd2) { setErr("Le password non coincidono"); return; }
    if (!/^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(pwd)) {
      setErr("Almeno 8 caratteri, con lettere e numeri"); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/users/reset-password`, { token, password: pwd });
      navigate("/auth?just_reset=1", { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Token non valido o scaduto");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return <Container>
      <div data-testid="reset-no-token" style={{ color: C.red, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.8rem" }}>
        Token mancante nell'URL.
      </div>
    </Container>;
  }

  return (
    <Container>
      <h1 style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900, fontSize: "1.6rem", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "0.4rem" }}>
        Imposta nuova <span style={{ color: C.accent }}>password</span>
      </h1>
      <p style={{ color: C.muted, fontSize: "0.82rem", lineHeight: 1.55, marginBottom: "1.4rem" }}>
        Scegli una password sicura: almeno 8 caratteri, lettere e numeri.
      </p>
      <form onSubmit={submit}>
        <div style={{ position: "relative", marginBottom: 8 }}>
          <Lock size={13} style={{ position: "absolute", left: 12, top: 12, color: C.muted }} />
          <input type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)}
            placeholder="Nuova password" data-testid="reset-pwd-1" style={inputStyle} />
        </div>
        <div style={{ position: "relative", marginBottom: 8 }}>
          <Lock size={13} style={{ position: "absolute", left: 12, top: 12, color: C.muted }} />
          <input type="password" required value={pwd2} onChange={(e) => setPwd2(e.target.value)}
            placeholder="Conferma password" data-testid="reset-pwd-2" style={inputStyle} />
        </div>
        {err && (
          <div data-testid="reset-error" style={{ marginTop: 8, padding: "0.5rem 0.7rem", background: "rgba(239,68,68,0.08)", border: `1px solid ${C.red}55`, color: C.red, fontSize: "0.75rem", fontFamily: "'JetBrains Mono',monospace" }}>
            ⚠ {err}
          </div>
        )}
        <button type="submit" disabled={busy} data-testid="reset-submit" style={btnPrimary}>
          {busy ? <Loader2 size={13} className="animate-spin" style={{ display: "inline" }} /> : "Imposta password"}
        </button>
      </form>
    </Container>
  );
}
